/**
 * The Little Prince inspired paper background
 *
 * Refined warm cream with subtle watercolor-paper texture.
 * Designed to feel like quality art paper — the kind you'd find
 * in a beautifully illustrated book.
 *
 * Colors come from CSS custom properties (themeTokens.ts) so they
 * adapt to light/dark mode automatically.
 */

// Warm cream with a subtle warm gradient — like watercolor paper
export const PAPER_BACKGROUND = `linear-gradient(170deg, var(--paper-bg-start) 0%, var(--paper-bg-mid) 40%, var(--paper-bg-end) 100%)`

/**
 * Ultra-subtle paper grain overlay using CSS-only noise pattern.
 * Mimics the slight texture of hot-pressed watercolor paper.
 * Apply to sx prop's '&::after' — parent needs position: relative.
 */
export const PAPER_TEXTURE_OVERLAY = {
  content: '""',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  backgroundImage: `
    radial-gradient(ellipse at 20% 50%, rgba(139, 115, 85, 0.03) 0%, transparent 70%),
    radial-gradient(ellipse at 80% 20%, rgba(180, 160, 130, 0.02) 0%, transparent 60%),
    repeating-linear-gradient(0deg, rgba(0,0,0,0.015) 0 1px, transparent 1px 4px),
    repeating-linear-gradient(90deg, rgba(0,0,0,0.015) 0 1px, transparent 1px 4px)
  `,
  mixBlendMode: 'multiply',
  opacity: 'var(--paper-texture-opacity)',
}

/**
 * Reader-specific page background (the area behind the paper)
 */
export const READER_PAGE_BACKGROUND = 'var(--reader-page-bg)'

/**
 * Cold-pressed cotton watercolor paper tooth — prominent, embossed feel.
 *
 * The key trick: grayscale noise blended via `overlay`. Overlay darkens
 * where the source pixel is below mid-gray and lightens where it's above —
 * so each noise bump renders with BOTH a shadow side and a lit side, the
 * way a real 3D pulp bump catches raking light. A plain multiply (what we
 * had before) only ever darkens, which is why the page looked dusty rather
 * than embossed.
 *
 * `type='fractalNoise'` outputs values centered around mid-gray (the right
 * pivot for overlay). `turbulence` would bias dark and lose the highlights.
 * Three octaves layer fine grain on irregular clusters — the non-uniform
 * "clumpy" look of cotton fibre. baseFrequency='0.20' puts features at ~5px
 * so the tooth is clearly visible at normal reading distance.
 *
 * The colorMatrix copies the R channel of the noise to all of R, G, B —
 * giving a true grayscale image, not the channel-shifted color noise that
 * feTurbulence outputs by default. Final alpha (0.65) sets the strength of
 * the emboss; raise toward 0.85 for more tooth, lower toward 0.45 for less.
 */
function buildPaperGrainUrl(seed: number, size = 720): string {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>` +
    // Pin the filter subregion to the rect exactly. Without this, the default
    // is 110% of the rect (10% padding) — `stitchTiles` then stitches over the
    // padded region, not the rect, and the browser-side `background-repeat`
    // tiles a rect whose edges don't line up. Result: a visible seam every
    // tile during scroll. With explicit 0/0/100%/100%, the noise stitches at
    // the same boundary the browser tiles at.
    `<filter id='g' x='0' y='0' width='100%' height='100%'>` +
    `<feTurbulence type='fractalNoise' baseFrequency='0.20' numOctaves='3' stitchTiles='stitch' seed='${seed}'/>` +
    `<feColorMatrix values='1 0 0 0 0  1 0 0 0 0  1 0 0 0 0  0 0 0 0 0.65'/>` +
    `</filter>` +
    `<rect width='100%' height='100%' filter='url(#g)'/>` +
    `</svg>`
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`
}

// Microscopic paper grain — rough, hand-crafted feel without visible lines.
export const READER_PAPER_GRAIN_URL_LIGHT = buildPaperGrainUrl(7)

/**
 * Large-scale fractal-noise "bloom" — slow, organic darker patches.
 *
 * Same primitive as the grain (feTurbulence), but at a much lower base
 * frequency so each "feature" is hundreds of pixels across — i.e. watercolor
 * blooms, not fibers.
 *
 * The alpha row of the colorMatrix is `0 0 0 ${alphaScale} ${alphaBias}`,
 * giving `A_out = alphaScale * A_in + alphaBias`. With `alphaScale=0.50` and
 * `alphaBias=-0.30`, only noise pixels with `A_in > 0.60` become visible —
 * so we see roughly the top 40% of the noise as sparse, organic patches.
 * Max visible alpha is 0.20: gentle.
 *
 * Unlike radial gradients in coprime tile heights, the pattern has no
 * rectangular tile-period structure inside a tile — the eye reads it as
 * truly irregular paper.
 */
function buildPaperBloomUrl(rgb: [number, number, number], alphaScale: number, alphaBias: number, seed = 11): string {
  const [r, g, b] = rgb
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='1536' height='1536'>` +
    // Explicit filter region matches the rect — see grain function for why.
    `<filter id='b' x='0' y='0' width='100%' height='100%'>` +
    `<feTurbulence type='fractalNoise' baseFrequency='0.0058' numOctaves='3' stitchTiles='stitch' seed='${seed}'/>` +
    `<feColorMatrix values='0 0 0 0 ${r}  0 0 0 0 ${g}  0 0 0 0 ${b}  0 0 0 ${alphaScale} ${alphaBias}'/>` +
    `</filter>` +
    `<rect width='100%' height='100%' filter='url(#b)'/>` +
    `</svg>`
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`
}

// Warm amber bloom — multiply against cream gives a subtle aged/water-stained look.
// alphaScale=0.35, alphaBias=-0.10: bloom visible everywhere noise > 0.29 (much
// lower threshold than before's 0.6) so we don't get "no-bloom" vertical strips
// where the noise happens to stay low along Y. Max alpha 0.25.
export const READER_PAPER_BLOOM_URL_LIGHT = buildPaperBloomUrl([0.42, 0.32, 0.18], 0.35, -0.1)

/**
 * Full reader paper texture, as an sx-style object.
 *
 * Apply to the scrolling content container (the element with `overflow: auto`).
 *
 * Two layers:
 *   - grain: cold-pressed paper tooth via overlay blend (720px tile, ~5px bumps)
 *   - bloom: low-frequency turbulence water-stain patches (1536px tile)
 *
 * The grain uses `overlay` so each noise bump gets a shadow AND a highlight
 * (embossed 3D pulp), while the bloom uses `multiply` for subtle warm patches.
 *
 * `backgroundAttachment: local` anchors the texture to the scrolling content,
 * so the page moves with the text — words feel printed on paper.
 */
export const READER_PAPER_BACKGROUND_SX = {
  backgroundColor: 'var(--reader-page-bg)',
  backgroundImage: ['var(--reader-paper-bloom)', 'var(--reader-paper-grain)'].join(', '),
  backgroundRepeat: 'repeat, repeat',
  backgroundSize: '1536px 1536px, 720px 720px',
  backgroundBlendMode: 'multiply, overlay',
  backgroundAttachment: 'local',
} as const
